// The Screen Context Frame (E01-S1-T3; contracts 1.0 section 1). The only thing perception emits.
import { z } from "zod";
import { ContextKey, Count, Id, IsoUtc, NonEmpty, PerceptionTier, RefId, strictObject, version, } from "../primitives.js";
import { CollectionObservation, FieldObservation } from "./observation.js";
import { EntityKind, TokenSchema } from "./values.js";
export const FrameSource = z.enum([
    "dom",
    "webview",
    "url",
    "sim",
    "vision",
    "desktop-ax",
    "host-sdk",
]);
export const MatchedBy = z.enum(["url", "signature", "semantic", "vision", "none"]);
export const Degraded = z.enum(["low-confidence", "atlas-mismatch", "excluded"]);
/** D-08: the confidence values recognition may produce. Fixtures pin them exactly. */
export const CONFIDENCE = {
    exact: 1.0,
    addressOnly: 0.9,
    signatureOnly: 0.8,
    degraded: 0.6,
    none: 0,
};
export const FrameScreen = strictObject({
    id: z.union([Id, z.literal("unknown")]),
    confidence: z.literal([
        CONFIDENCE.exact,
        CONFIDENCE.addressOnly,
        CONFIDENCE.signatureOnly,
        CONFIDENCE.degraded,
        CONFIDENCE.none,
    ]),
    matchedBy: MatchedBy,
    degraded: Degraded.optional(),
    context: z.record(ContextKey, z.string()).optional(),
});
export const FrameEntity = strictObject({ kind: EntityKind, token: TokenSchema });
export const Redactions = strictObject({ read: Count, tokenized: Count, masked: Count });
const FrameShape = strictObject({
    v: version(1),
    seq: Count,
    sourceId: NonEmpty,
    at: IsoUtc,
    source: FrameSource,
    tier: PerceptionTier,
    app: strictObject({ id: Id, atlasVersion: NonEmpty, variant: NonEmpty }),
    screen: FrameScreen,
    overlays: z.array(strictObject({ id: Id })),
    entities: z.array(FrameEntity),
    fields: z.record(RefId, FieldObservation),
    missing: z.array(RefId),
    collections: z.record(RefId, CollectionObservation),
    focus: strictObject({ target: Id }).optional(),
    signals: z.array(Id),
    redactions: Redactions,
});
const empty = (o) => Object.keys(o).length === 0;
/**
 * Cross-field rules the prose states. Rules that need the atlas are in conform.ts (E01-S1-T6).
 */
export const ScreenContextFrame = FrameShape.superRefine((f, ctx) => {
    const issue = (path, message) => {
        ctx.addIssue({ code: "custom", path, message });
    };
    const unknown = f.screen.id === "unknown";
    if (unknown !== (f.screen.matchedBy === "none"))
        issue(["screen", "id"], "screen.id is unknown if and only if matchedBy is none");
    if (unknown) {
        if (f.screen.confidence !== 0)
            issue(["screen", "confidence"], "an unknown screen has confidence 0");
        if (!empty(f.fields))
            issue(["fields"], "an unknown screen carries no fields");
        if (!empty(f.collections))
            issue(["collections"], "an unknown screen carries no collections");
        if (f.screen.context !== undefined)
            issue(["screen", "context"], "an unknown screen carries no context");
    }
    if (f.screen.confidence === CONFIDENCE.degraded && f.screen.degraded === undefined)
        issue(["screen", "degraded"], "a screen at 0.6 is degraded: low-confidence (D-08)");
    if (f.screen.degraded !== undefined) {
        if (!empty(f.fields))
            issue(["fields"], "nothing on a degraded screen is read");
        if (!empty(f.collections))
            issue(["collections"], "nothing on a degraded screen is read");
    }
    if (f.tier === 0) {
        if (!empty(f.fields))
            issue(["fields"], "tier 0 carries no fields");
        if (!empty(f.collections))
            issue(["collections"], "tier 0 carries no collections");
        if (f.overlays.length)
            issue(["overlays"], "tier 0 carries no overlays");
        if (f.entities.length)
            issue(["entities"], "tier 0 carries no entities");
        if (f.signals.length)
            issue(["signals"], "tier 0 carries no signals");
        if (f.focus)
            issue(["focus"], "tier 0 carries no focus");
        if (f.screen.confidence > 0.9)
            issue(["screen", "confidence"], "tier 0 recognises by address alone: ceiling 0.9");
    }
    if (f.tier < 2) {
        if (f.entities.length)
            issue(["entities"], "entity tokens appear at tier 2 and above");
        for (const [id, obs] of Object.entries(f.fields)) {
            if (obs.cls === "identifier")
                issue(["fields", id], "an identifier observation appears at tier 2 and above");
            if (obs.cls === "numeric" && obs.band !== undefined)
                issue(["fields", id, "band"], "a numeric band appears at tier 2 and above");
        }
    }
    for (const id of f.missing)
        if (id in f.fields)
            issue(["missing"], `${id} is both observed and missing`);
});
//# sourceMappingURL=frame.js.map